import $ from "jquery";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as modalController from "modalController";
import * as utils from "shared/utils";
import * as buttonRole from "ui/buttonRole";

const animateTime = 200;
// A little more than a closing sheet's slide, in _dialogs.scss
const sheetSlideTime = 500;
const VERTICAL_SPACING = 15;
const focusableSelector =
  "a[href], button, input:not([type=hidden]), select, textarea, iframe, [tabindex]";

type BaseModalEntry = {
  id: number;
  options: Required<
    Pick<
      modalController.ModalOptions,
      "center" | "clickToCancel" | "closeButton" | "minHeight"
    >
  > &
    modalController.ModalOptions;
  /**
   * Where focus goes back to as a dialog closes, the first still on the page:
   * what had it as the dialog opened, then, if that was in another dialog,
   * where that one's would go
   */
  returnFocusTo: HTMLElement[];
};

type ComponentModalEntry = BaseModalEntry & {
  component: preact.ComponentChild;
  kind: "component";
};

type LegacyModalEntry = BaseModalEntry & {
  element: HTMLElement;
  kind: "legacy";
  oldCss?: Record<string, string>;
  oldParent?: JQuery<HTMLElement>;
};

type ModalEntry = ComponentModalEntry | LegacyModalEntry;

type State = {
  stack: ModalEntry[];
  /** Sheets sliding away, out of the stack, rendered where they were */
  closing: ComponentModalEntry[];
};

const defaultOptions = {
  center: true,
  clickToCancel: true,
  closeButton: true,
  minHeight: 50,
};

const modalTitle = (entry: ModalEntry) => entry.options.title ?? "No-title";

interface Props {
  /**
   * Dialogs fill the screen on phones, as sheets sliding up (the app's own
   * pages, not the Web Reader's toolbar), except those that ask for it not to
   */
  sheetsOnPhones?: boolean;
}

export default class ModalHostView
  extends preact.Component<Props, State>
  implements modalController.ModalHost
{
  private nextID = 1;
  private onShowTimeoutID?: number;
  private backgroundElement?: HTMLDivElement;

  state: State = {
    stack: [],
    closing: [],
  };

  componentDidMount() {
    modalController.registerHost(this);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentDidUpdate(_previousProps: Props, previousState: State) {
    const previousTop = previousState.stack[previousState.stack.length - 1];
    const currentTop = this.state.stack[this.state.stack.length - 1];
    const hasCenteredModal = this.state.stack.some(
      (entry) => entry.options.center
    );

    document.body.classList.toggle("modalNoOverflow", hasCenteredModal);
    modalController.fullScreenShown.value = this.state.stack.some(
      (entry) => entry.options.fullScreen
    );

    if (!currentTop) {
      modalController.requestModalSpace(0);
    }

    if (
      previousTop &&
      !this.state.stack.includes(previousTop) &&
      (!currentTop || previousState.stack.includes(currentTop))
    ) {
      this.returnFocus(previousTop);
    }

    if (currentTop?.id !== previousTop?.id) {
      window.clearTimeout(this.onShowTimeoutID);
      this.hideLegacyEntry(previousTop);
      if (currentTop) {
        this.prepareLegacyEntry(currentTop);
        this.onShowTimeoutID = window.setTimeout(() => {
          this.showCurrentEntry(currentTop);
        }, 100);
      }
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.onShowTimeoutID);
    for (const entry of this.state.stack) {
      this.removeEntry(entry, true);
    }
    document.body.classList.remove("modalNoOverflow");
    modalController.fullScreenShown.value = false;
    document.removeEventListener("keydown", this.handleKeyDown);
    modalController.unregisterHost(this);
  }

  show(
    component: preact.ComponentChild,
    options?: modalController.ModalOptions
  ) {
    this.setState((previousState) => ({
      stack: [
        ...previousState.stack,
        {
          component,
          id: this.nextID,
          kind: "component",
          returnFocusTo: this.focusToReturnTo(),
          options: {
            ...defaultOptions,
            ...options,
            fullScreen:
              options?.fullScreen ??
              (this.props.sheetsOnPhones &&
                deviceDetect.isSmall() &&
                options?.center !== false &&
                !options?.compact),
          },
        },
      ],
    }));
    this.nextID += 1;
  }

  showLegacy(element: HTMLElement, options?: modalController.ModalOptions) {
    this.setState((previousState) => ({
      stack: [
        ...previousState.stack,
        {
          element,
          id: this.nextID,
          kind: "legacy",
          returnFocusTo: this.focusToReturnTo(),
          options: {
            ...defaultOptions,
            ...options,
          },
        },
      ],
    }));
    this.nextID += 1;
  }

  cancel(event?: any, skipCallback?: boolean) {
    const top = this.state.stack[this.state.stack.length - 1];
    if (!top) {
      return false;
    }

    if (
      top.options.confirmCancelCallback &&
      !top.options.confirmCancelCallback()
    ) {
      return true;
    }

    this.slideSheetAway(top);
    this.removeEntry(top, false);

    this.setState((previousState) => ({
      stack: previousState.stack.slice(0, -1),
    }));

    if (!skipCallback) {
      top.options.cancelCallback?.();
    }

    if (event) {
      event.stopPropagation();
    }

    return true;
  }

  cancelAll() {
    if (this.state.stack.length === 0) {
      return false;
    }

    const entriesToRemove: ModalEntry[] = [];
    for (const entry of [...this.state.stack].reverse()) {
      if (!entry.options.clickToCancel) {
        continue;
      }
      if (
        entry.options.confirmCancelCallback &&
        !entry.options.confirmCancelCallback()
      ) {
        break;
      }
      entriesToRemove.push(entry);
      entry.options.cancelCallback?.();
    }

    for (const entry of entriesToRemove) {
      this.removeEntry(entry, false);
    }

    this.setState((previousState) => ({
      stack: previousState.stack.filter(
        (entry) => !entry.options.clickToCancel
      ),
    }));
    return true;
  }

  isShown() {
    return this.state.stack.length > 0;
  }

  isHostConnected() {
    return Boolean((this.base as Element | undefined)?.isConnected);
  }

  private handleBackgroundClick = (
    event: preact.JSX.TargetedMouseEvent<HTMLDivElement>
  ) => {
    const top = this.state.stack[this.state.stack.length - 1];
    if (!top || event.target !== event.currentTarget) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (top.options.clickToCancel) {
      this.cancel(event);
    }
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    const top = this.state.stack[this.state.stack.length - 1];
    if (top?.options.clickToCancel && event.key === "Escape") {
      this.cancel(event);
    }
    if (top?.options.center && event.key === "Tab") {
      this.keepTabInside(top, event);
    }
  };

  /** The dialog box of a modal (it may be a tooltip or dropdown, if legacy) */
  private dialogElement(entry: ModalEntry) {
    return entry.kind === "legacy"
      ? entry.element
      : (this.base as Element | undefined)?.querySelector<HTMLElement>(
          `[data-modal-entry-id="${entry.id}"] .centralDialog`
        ) ?? undefined;
  }

  private focusToReturnTo() {
    const focused = document.activeElement;
    if (!(focused instanceof HTMLElement)) {
      return [];
    }
    const dialogEntry = this.state.stack.find((entry) =>
      this.dialogElement(entry)?.contains(focused)
    );
    return [focused, ...(dialogEntry?.returnFocusTo ?? [])];
  }

  /**
   * A dialog takes focus as it opens, unless it took it itself (onShow): to
   * its control marked autofocus, if any, or to itself, off the page behind
   */
  private focusInto(entry: ModalEntry) {
    const dialog = this.dialogElement(entry);
    if (!dialog || dialog.contains(document.activeElement)) {
      return;
    }
    const control = dialog.querySelector<HTMLElement>("[autofocus]");
    if (control) {
      control.focus();
    } else {
      // Not scrolling a long dialog (a sheet of languages) to its middle
      dialog.focus({ preventScroll: true });
    }
  }

  /** Tab and Shift+Tab go round a dialog's controls, not the page's behind */
  private keepTabInside(entry: ModalEntry, event: KeyboardEvent) {
    const dialog = this.dialogElement(entry);
    if (!dialog) {
      return;
    }
    const tabbable = [
      ...dialog.querySelectorAll<HTMLElement>(focusableSelector),
    ].filter(
      (element) =>
        element.tabIndex >= 0 &&
        !(element as HTMLButtonElement).disabled &&
        element.getClientRects().length > 0
    );
    const [first, last] = event.shiftKey
      ? [tabbable[tabbable.length - 1], tabbable[0]]
      : [tabbable[0], tabbable[tabbable.length - 1]];
    const focused = document.activeElement;
    if (
      !first ||
      focused === last ||
      focused === dialog ||
      !dialog.contains(focused)
    ) {
      event.preventDefault();
      (first ?? dialog).focus();
    }
  }

  private returnFocus(entry: ModalEntry) {
    if (entry.options.center) {
      entry.returnFocusTo
        .find((element) => element.isConnected)
        ?.focus({ preventScroll: true });
    }
  }

  /**
   * A full-screen modal, a sheet on phones, slides back down as it closes, as
   * iOS's do. It stays rendered in its place till then, out of the stack, so
   * that its contents (iframes, selects, scroll position) are left as they are
   */
  private slideSheetAway(entry: ModalEntry) {
    if (
      entry.kind !== "component" ||
      !entry.options.fullScreen ||
      !deviceDetect.isSmall()
    ) {
      return;
    }
    // Ahead of any still closing, as they were above it in the stack
    this.setState((previousState) => ({
      closing: [entry, ...previousState.closing],
    }));
    window.setTimeout(() => {
      this.setState((previousState) => ({
        closing: previousState.closing.filter((other) => other !== entry),
      }));
    }, sheetSlideTime);
  }

  private hideLegacyEntry(entry?: ModalEntry) {
    if (entry?.kind !== "legacy") {
      return;
    }

    const element = $(entry.element).hide();
    if (entry.options.center) {
      element.detach();
    }
  }

  private prepareLegacyEntry(entry?: ModalEntry) {
    if (entry?.kind !== "legacy") {
      return;
    }

    const element = $(entry.element).css({ "z-index": 250 });

    if (entry.options.center && this.backgroundElement) {
      this.backgroundElement.append(entry.element);
    }

    if (entry.options.addToBody && !entry.oldParent) {
      element.css({ opacity: 0 });
      element.show();

      entry.oldParent = element.parent();
      entry.oldCss = {
        left: element.css("left"),
        position: element.css("position"),
        top: element.css("top"),
      };

      const top = element.offset()?.top ?? 0;
      const left = element.offset()?.left ?? 0;

      document.body.append(entry.element);
      element.css({
        left,
        opacity: 1,
        position: "absolute",
        top,
      });
    }
  }

  private showCurrentEntry(entry?: ModalEntry) {
    if (!entry) {
      return;
    }

    if (entry.kind === "legacy") {
      const element = $(entry.element);
      element.fadeIn(animateTime);

      entry.options.view?.setFocus?.();
    }

    entry.options.onShow?.();
    if (entry.options.center) {
      this.focusInto(entry);
    }
    this.requestSpaceForEntry(entry);
  }

  private removeEntry(entry: ModalEntry, skipViewRemove: boolean) {
    if (entry.kind !== "legacy") {
      return;
    }

    const element = $(entry.element);

    if (entry.options.center) {
      element.remove();
    } else {
      element.hide();
    }

    if (entry.options.addToBody && entry.oldParent) {
      element.css(entry.oldCss ?? {});
      entry.oldParent.append(element);
    }

    if (!skipViewRemove) {
      entry.options.view?.remove?.();
    }
  }

  private requestSpaceForEntry(entry: ModalEntry) {
    const dialog = this.dialogElement(entry);
    if (!dialog) {
      return;
    }
    const element = $(dialog);

    const outerHeight = Math.max(
      entry.options.minHeight,
      element.outerHeight() ?? 0
    );

    modalController.requestModalSpace(
      (entry.options.center
        ? 0
        : (element.offset()?.top ?? 0) + VERTICAL_SPACING) +
        outerHeight +
        VERTICAL_SPACING
    );
  }

  private renderDialog(entry: ModalEntry) {
    const { options } = entry;
    const style: preact.JSX.CSSProperties = {
      display: "block",
      // Full-screen modals fill the screen, from their stylesheet
      minHeight: options.fullScreen ? undefined : options.minHeight,
      zIndex: 250,
    };

    if (options.width && !options.fullScreen) {
      style.maxWidth = options.width;
      style.minWidth = options.width;
      style.width = options.width;
      style.marginLeft = Math.round(-0.5 * options.width);
    }

    return (
      <div
        class={[
          "centralDialog",
          options.fullScreen ? "fullScreen" : "",
          options.wide ? "wide" : "",
          options.undoZoom ? "undoZoom" : "",
          options.className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-modal-title={modalTitle(entry)}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={style}
      >
        {options.closeButton && (
          <div
            {...buttonRole.props}
            class="closeDialogButton"
            aria-label="Close"
            onClick={(event) => this.cancel(event)}
          >
            &nbsp;<i class="icon icon-remove"></i>&nbsp;
          </div>
        )}
        {entry.kind === "component" ? entry.component : null}
      </div>
    );
  }

  render() {
    const { stack, closing } = this.state;
    if (stack.length === 0 && closing.length === 0) {
      return <div data-modal-host style={{ display: "none" }} />;
    }

    const top: ModalEntry | undefined = stack[stack.length - 1];

    return (
      <div
        id="modalBackground"
        // Only closing sheets left: the dimming fades out as they slide away
        class={utils.cn({
          fullScreen: top ? top.options.fullScreen : true,
          closing: !top,
        })}
        data-anim="fadeIn"
        ref={(element) => {
          this.backgroundElement = element ?? undefined;
        }}
        onClick={this.handleBackgroundClick}
        style={{ display: "block" }}
      >
        {/* Render every stacked component entry (hiding non-top ones via display:none)
            instead of only the top, so parent modals stay mounted when a sub-modal opens.
            Unmounting the parent loses its component state (e.g. EditCardView's setState
            calls were lost when the Generate Context sub-modal opened on top). */}
        {[...stack, ...closing].map((entry) => {
          if (entry.kind !== "component") return null;
          const isClosing = closing.includes(entry);
          return (
            <div
              key={entry.id}
              data-modal-entry-id={entry.id}
              class={isClosing ? "closingSheet" : undefined}
              style={{
                display: entry === top || isClosing ? undefined : "none",
              }}
            >
              {this.renderDialog(entry)}
            </div>
          );
        })}
      </div>
    );
  }
}
